import {Box, Typography} from '@mui/material';
import './IssueCodesContent.scss';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ReactComponent as AddIcon} from '../../../../../../assets/images/add.svg';
import {ReactComponent as DeleteIcon} from '../../../../../../assets/images/delete-chat.svg';
import {t} from 'i18next';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {useRef, useState} from 'react';
import IssueCodeDeleteModal from './issue-code-delete-modal/IssueCodeDeleteModal';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import {v4 as uuidv4} from 'uuid';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import {IssueCode} from '../../../../../../api/types/aiReview.type';
import {useReviewHeaderContext} from '../../../header/ReviewHeader';

export interface IssueCodeContentProps {
  issueCodeData: IssueCode[];
  setIssueCodeData: React.Dispatch<React.SetStateAction<IssueCode[]>>;
  nameErrors: Record<string, boolean>;
  hasValidationErrors: boolean;
  showEmptyErrors: boolean;
  setShowEmptyErrors: React.Dispatch<React.SetStateAction<boolean>>;
  touchedTile: Record<string, boolean>;
  handleTileBlur: (id: string) => void;
}

const IssueCodeContent = ({
  issueCodeData,
  setIssueCodeData,
  nameErrors,
  hasValidationErrors,
  showEmptyErrors,
  setShowEmptyErrors,
  touchedTile,
  handleTileBlur,
}: IssueCodeContentProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const newlyAddedIdRef = useRef<string | null>(null);
  const {setIsProtocolUpdated} = useReviewHeaderContext();
  const handleAddIssueCode = () => {
    setShowEmptyErrors(false);
    const newId = uuidv4();
    newlyAddedIdRef.current = newId;
    setIssueCodeData((prev) => [...prev, {id: newId, code: '', question: ''}]);
    setIsProtocolUpdated(true);
  };

  const handleIssueCodeDelete = (id: string) => {
    setSelectedId(id);
  };

  const handleIssueCodeModalClose = () => {
    setSelectedId(null);
  };

  const handleConfirmIssueCodeDelete = () => {
    setIssueCodeData((prev) => prev.filter((tile) => tile.id !== selectedId));
    setIsProtocolUpdated(true);
    handleIssueCodeModalClose();
  };

  const handleIssueCodeChange = (
    id: string,
    field: 'code' | 'question',
    value: string
  ) => {
    setShowEmptyErrors(false);
    setIssueCodeData((prev) =>
      prev.map((tile) => (tile.id === id ? {...tile, [field]: value} : tile))
    );
    setIsProtocolUpdated(true);
  };

  return (
    <Box className="issue-code-content-container">
      {issueCodeData.length === 0 ? (
        <IssueCodeContentFallBack handleAddIssueCode={handleAddIssueCode} />
      ) : (
        <Box className="issue-code-content" data-testid="issue-code-content">
          <ArrButton
            className="add-icon-btn"
            dataTestId="add-icon-btn"
            size="small"
            onClick={handleAddIssueCode}
            disabled={hasValidationErrors}
            startIcon={<AddIcon className="add-icon" data-testid="add-icon" />}
          >
            {t('ai.review.reviewAndApprove.issueCodeBtnTxt')}
          </ArrButton>
          {issueCodeData.map((tile, index) => (
            <Box
              key={tile.id}
              className={`issue-code-tile-container 
                ${newlyAddedIdRef.current === tile.id ? 'issue-code-new-tile' : ''}
                ${
                  (touchedTile[tile.id!] || showEmptyErrors) &&
                  tile.code?.trim() === '' &&
                  tile.question?.trim() === ''
                    ? 'issue-code-tile--error'
                    : ''
                }`}
              data-testid={`issue-code-fields-${index}`}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  newlyAddedIdRef.current = null;
                  handleTileBlur(tile.id!);
                }
              }}
            >
              <Box className="issue-code-name-container">
                <ArrTextField
                  value={tile.code}
                  className="issue-code-name-field"
                  dataTestId={`issue-code-name-field-${index}`}
                  slotProps={{
                    input: {
                      inputProps: {maxLength: 50},
                    },
                  }}
                  autoFocus
                  placeholder={t(
                    'ai.review.reviewAndApprove.issueCodeNameField'
                  )}
                  state={
                    nameErrors[tile.id!] ||
                    ((touchedTile[tile.id!] || showEmptyErrors) &&
                      !tile.code?.trim() &&
                      tile.question.trim() !== '')
                      ? 'error'
                      : 'enabled'
                  }
                  showErrorIcon={true}
                  supportText={
                    nameErrors[tile.id!]
                      ? t('ai.review.newJobDetail.splCharError')
                      : (touchedTile[tile.id!] || showEmptyErrors) &&
                          !tile.code?.trim() &&
                          tile.question.trim() !== ''
                        ? t('ai.review.newJobDetail.issueCodeNameRequired')
                        : `${tile.code?.length ?? 0} / 50`
                  }
                  onChange={(e) =>
                    handleIssueCodeChange(tile.id!, 'code', e.target.value)
                  }
                />
                <Box className="name-icon-container">
                  {(touchedTile[tile.id!] || showEmptyErrors) &&
                    tile.code?.trim() === '' &&
                    tile.question?.trim() === '' && (
                      <ArrIconButton
                        className="warning-icon-btn"
                        icon={<WarningOutlinedIcon />}
                        ariaLabel="warning-icon-btn"
                        shape="default"
                        size="small"
                        testId={`warning-icon-btn-${index}`}
                      />
                    )}

                  <ArrIconButton
                    className="delete-icon-btn"
                    icon={<DeleteIcon />}
                    onClick={() => handleIssueCodeDelete(tile.id!)}
                    ariaLabel="delete-icon-btn"
                    shape="default"
                    size="small"
                    testId={`delete-icon-btn-${index}`}
                  />
                </Box>
              </Box>
              <ArrTextArea
                value={tile.question}
                className="issue-code-question-field"
                dataTestId={`issue-code-question-field-${index}`}
                placeholder={t('ai.review.reviewAndApprove.issueCodeQtnField')}
                error={
                  touchedTile[tile.id!] &&
                  !tile.question?.length &&
                  tile.code?.trim() !== ''
                }
                onChange={(value) =>
                  handleIssueCodeChange(tile.id!, 'question', value)
                }
                maxLength={1000}
                helperText={`${touchedTile[tile.id!] && !tile.question?.length && tile.code?.trim() !== '' ? t('ai.review.reviewAndApprove.issueCodeQtnFieldError') : `${tile.question?.length ?? 0} / 1,000`}`}
              />
            </Box>
          ))}
        </Box>
      )}
      <IssueCodeDeleteModal
        open={selectedId !== null}
        onClose={handleIssueCodeModalClose}
        onConfirm={handleConfirmIssueCodeDelete}
      />
      {showEmptyErrors && (
        <ArrToastNotification
          title={t('ai.review.reviewAndApprove.issueCodeBannerTitle')}
          message={t('ai.review.reviewAndApprove.isueCodeBannerDesc')}
          onClose={() => setShowEmptyErrors(false)}
          status={[ToastType.Warning]}
          data-testid="toast-message"
          aria-label={t('ai.review.reviewAndApprove.issueCodeBannerTitle')}
        />
      )}
    </Box>
  );
};

export default IssueCodeContent;

export const IssueCodeContentFallBack = ({
  handleAddIssueCode,
  displayButton = true,
}: {
  handleAddIssueCode?: () => void;
  displayButton?: boolean;
}) => (
  <Box
    className="issue-code-empty-content"
    data-testid="issue-code-empty-content"
  >
    <Box className="issue-code-empty-content-container">
      <Typography
        className="typo-text subhead3_size issue-code-empty-title"
        data-testid={`issue-codes-content-issue-code-empty-title`}
      >
        {t('ai.review.reviewAndApprove.issueCodeEmptyTitle')}
      </Typography>
      <Typography
        className="issue-code-empty-desc typo-text body3_size text-body-strong"
        data-testid={`issue-codes-content-issue-code-empty-desc`}
      >
        {t('ai.review.reviewAndApprove.issueCodeEmptyDesc')}
      </Typography>
    </Box>
    {displayButton && (
      <ArrButton
        className="add-icon-btn"
        size="small"
        onClick={handleAddIssueCode}
        startIcon={<AddIcon className="add-icon" data-testid="add-icon" />}
        dataTestId="issue-codes-content-issue-code-btn-txt-button"
      >
        {t('ai.review.reviewAndApprove.issueCodeBtnTxt')}
      </ArrButton>
    )}
  </Box>
);
